/**
 * Base API Service
 * Handles all HTTP requests with interceptors for authentication and error handling
 */

import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse, AxiosError } from 'axios';
import { API_CONFIG } from '../config/api.config';
import { tokenStorage } from '../utils/storage';

class ApiService {
  private api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      baseURL: API_CONFIG.baseURL,
      timeout: API_CONFIG.timeout,
      headers: API_CONFIG.headers,
    });

    this.setupInterceptors();
  }

  /**
   * Setup request and response interceptors
   */
  private setupInterceptors(): void {
    // Request interceptor - Add auth token to headers
    this.api.interceptors.request.use(
      (config) => {
        const token = tokenStorage.getToken();
        if (token && config.headers) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error: AxiosError) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor - Handle errors globally
    this.api.interceptors.response.use(
      (response: AxiosResponse) => response,
      async (error: AxiosError) => {
        const originalRequest = error.config as AxiosRequestConfig & { _retry?: boolean };

        // Handle 401 Unauthorized - Token expired
        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;

          try {
            // Try to refresh the token
            const refreshToken = tokenStorage.getRefreshToken();
            
            if (refreshToken) {
              // Attempt to refresh the access token
              const response = await this.api.post('/auth/refresh', {
                refreshToken,
              });

              const { token, refreshToken: newRefreshToken } = response.data;

              // Save new tokens
              tokenStorage.setToken(token);
              if (newRefreshToken) {
                tokenStorage.setRefreshToken(newRefreshToken);
              }

              // Update the authorization header
              if (originalRequest.headers) {
                originalRequest.headers.Authorization = `Bearer ${token}`;
              }

              // Retry the original request
              return this.api(originalRequest);
            }
          } catch (refreshError) {
            // Refresh failed, clear tokens and redirect to login
            console.error('Token refresh failed:', refreshError);
          }

          // Clear tokens and redirect to login
          tokenStorage.clearAll();
          window.location.href = '/login';
          return Promise.reject(error);
        }

        // Handle 403 Forbidden
        if (error.response?.status === 403) {
          console.error('Access forbidden - insufficient permissions');
        }

        // Handle 429 Too Many Requests — let it propagate so callers can show specific UI
        if (error.response?.status === 429) {
          console.warn('Rate limit reached');
        }

        // Handle 404 Not Found
        if (error.response?.status === 404) {
          console.error('Resource not found');
        }

        // Handle 500 Server Error
        if (error.response?.status === 500) {
          console.error('Internal server error');
        }

        return Promise.reject(error);
      }
    );
  }

  /**
   * GET request
   */
  public async get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response: AxiosResponse<T> = await this.api.get(url, config);
    return response.data;
  }

  /**
   * POST request
   */
  public async post<T, D = unknown>(url: string, data?: D, config?: AxiosRequestConfig): Promise<T> {
    const response: AxiosResponse<T> = await this.api.post(url, data, config);
    return response.data;
  }

  /**
   * PUT request
   */
  public async put<T, D = unknown>(url: string, data?: D, config?: AxiosRequestConfig): Promise<T> {
    const response: AxiosResponse<T> = await this.api.put(url, data, config);
    return response.data;
  }

  /**
   * PATCH request
   */
  public async patch<T, D = unknown>(url: string, data?: D, config?: AxiosRequestConfig): Promise<T> {
    const response: AxiosResponse<T> = await this.api.patch(url, data, config);
    return response.data;
  }

  /**
   * DELETE request
   */
  public async delete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response: AxiosResponse<T> = await this.api.delete(url, config);
    return response.data;
  }

  /**
   * Get API instance for advanced usage
   */
  public getApiInstance(): AxiosInstance {
    return this.api;
  }
}

// eslint-disable-next-line import/no-anonymous-default-export
export default new ApiService();

